import { isNumeric } from '../../util/helpers';


/**
 * Fuel type
 */
export enum FuelType {
  /**
   * 100LL
   */
  _100LL = '100LL',

  /**
   * Jet-A
   */
  JetA = 'Jet-A',

  /**
   * Jet-A Premix
   */
  JetAPremix = 'Jet-A-Premix/Prist',

  /**
   * Automobile gas (aka, "mogas")
   */
  AutoGas = 'AutoGas',

  /**
   * MOGAS
   */
  MOGAS = 'MOGAS',

  /**
   * Swift Fuels
   */
  SwiftFuels = 'Swift Fuels',

  /**
   * UL 94
   */
  UL94 = 'UL94'

}


/**
 * Fuel service type (full-serve vs self-serve)
 */
export enum ServiceType {
  FullServe,
  SelfServe
}

/**
 * Model for storing fuel type and price for a business
 */
export class BusinessFuel {
  /**
   * Unique identifier for this record in the database
   */
  id: number;

  /**
   * Business ID for which this record applies
   */
  businessId: number;

  /**
   * Source ID that identifies the source of this data (e.g., airport manager)
   */
  sourceId: number;

  /**
   * Fuel brand
   */
  fuelBrand: string;

  /**
   * Fuel type (100LL, Jet-A, etc.)
   */
  fuelType: FuelType;

  /**
   * Fuel service type (full-serve vs self-serve)
   */
  service: ServiceType;

  /**
   * Restrictions or other information about fuel availability (e.g., "fuel club only" or "volume discount available")
   */
  otherInfo: string;

  /**
   * Fuel price (per gallon)
   */
  price: number;

  /**
   * Date record was last updated
   */
  updateDate: Date;

  /**
   * User ID who last updated the record
   */
  updateBy: number;

  /**
   * Parses business fuel
   * @param data {any} Business fuel data as returned by API
   * @return {BusinessFuel} Parsed business fuel
   */
  static parse(data: any): BusinessFuel {
    return Object.assign(new BusinessFuel(), {
      id: data.fuelId,
      businessId: data.businessId,
      sourceId: data.sourceId,
      fuelBrand: data.fuelBrand || null,
      fuelType: this.parseFuelType(data.fuelType),
      service: this.parseServiceType(data.service),
      otherInfo: data.forWho || null,
      price: isNumeric(data.price) ? +data.price : null,
      updateDate: data.updateDate ? new Date(data.updateDate) : null,
      updateBy: data.updateBy
    });
  }

  /**
   * Parses fuel type
   * @param value {any} Fuel type as returned by API
   * @return {FuelType} Parsed fuel type
   */
  static parseFuelType(value: any): FuelType {
    switch (value) {
      case '100LL':
        return FuelType._100LL;
      case 'Jet-A-Premix/Prist':
        return FuelType.JetAPremix;
      case 'Jet-A':
        return FuelType.JetA;
      case 'JetA':
        return FuelType.JetA;
      case 'AutoGas':
        return FuelType.AutoGas;
      case 'MOGAS':
        return FuelType.MOGAS;
      case 'UL94':
        return FuelType.UL94;
      case 'Swift Fuels':
        return FuelType.SwiftFuels;
      default:
        return null;
    }
  }

  /**
   * Returns formatted fuel type
   */
  static formatFuelType(value: FuelType): string {
    switch (value) {
      case FuelType._100LL:
        return '100LL';
      case FuelType.JetAPremix:
        return 'Jet-A-Premix/Prist';
      case FuelType.JetA:
        return 'Jet-A';
      case FuelType.AutoGas:
        return 'AutoGas';
      case FuelType.SwiftFuels:
        return 'Swift Fuels';
      case FuelType.UL94:
        return 'UL94';
      case FuelType.MOGAS:
        return 'MOGAS';
      default:
        return null;
    }
  }

  /**
 * Parses service type
 * @param value {any} Service type as returned by API
 * @return {ServiceType} Parsed service type
 */
  static parseServiceType(value: any): ServiceType {
    const v = (value || '').toUpperCase();
    if (v.startsWith('FULL')) {
      return ServiceType.FullServe;
    }
    if (v.startsWith('SELF')) {
      return ServiceType.SelfServe;
    }
    return null;
  }

  /**
   * Returns formatted service type
   */
  static formatServiceType(value: ServiceType): string {
    switch (value) {
      case ServiceType.FullServe:
        return 'Full Service';
      case ServiceType.SelfServe:
        return 'Self Service';
      default:
        return null;
    }
  }

  /**
   * Returns formatted service type as a shortened string
   */
  static formatServiceTypeShort(value: ServiceType): string {
    switch (value) {
      case ServiceType.FullServe:
        return 'full';
      case ServiceType.SelfServe:
        return 'self';
      default:
        return null;
    }
  }
}
